import { api } from '../api.js'

/**
 * Signierte Download-URLs — gebündelt statt einzeln (Anf. 66/76).
 *
 * ## Warum es das gibt
 *
 * Eine Galerie mit sechzig Vorschaubildern rief bis Phase 14 sechzigmal
 * `GET /blobs/:id/url` auf, jede Kachel für sich. Ein Browser hält je Host nur
 * eine Handvoll Verbindungen offen; der Rest wartet. Auf einem alten Telefon
 * über Mobilfunk kostet allein das Anstehen mehr Zeit als die Bilder selbst.
 *
 * Diese Datei sammelt alle Anfragen, die innerhalb eines kurzen Fensters
 * eintreffen, und stellt **einen** Aufruf daraus. Wer eine einzelne URL
 * braucht, merkt davon nichts — die Funktion sieht aus wie vorher.
 *
 * ## Warum ein Zwischenspeicher, und warum ein kurzer
 *
 * Eine signierte URL gilt fünf Minuten. Sie danach noch
 * einmal auszuliefern wäre ein Bild, das nicht lädt, ohne dass jemand versteht
 * warum. Deshalb verfällt der Eintrag **vor** der Signatur, nicht mit ihr:
 * Lieber einmal zu früh neu signieren als einmal zu spät.
 */

/** Sammelfenster. Lang genug für ein Raster, kurz genug für ein einzelnes Bild. */
const FENSTER_MS = 30

/** Muss zu `URLS_MAX` im Server passen — mehr nimmt der Endpunkt nicht an. */
const MAX_JE_AUFRUF = 60

/**
 * Sicherheitsabstand zur Signaturfrist. Eine URL, die beim Anzeigen noch
 * dreißig Sekunden hat, ist eine URL, die funktioniert.
 */
const PUFFER_MS = 30_000

interface Eintrag { url: string; gueltigBis: number }

const speicher = new Map<string, Eintrag>()
const offen = new Map<string, Array<(url: string | null) => void>>()
let geplant: ReturnType<typeof setTimeout> | null = null

/**
 * Eine signierte URL. `null`, wenn der Blob nicht (mehr) sichtbar ist.
 *
 * Kein Fehler im Rückgabewert: Ein fehlendes Vorschaubild ist ein Platzhalter,
 * kein Grund, ein Raster mit einer Fehlermeldung zu überziehen.
 */
export function blobUrl(blobId: string): Promise<string | null> {
  const da = speicher.get(blobId)
  if (da && da.gueltigBis > Date.now()) return Promise.resolve(da.url)

  return new Promise((aufloesen) => {
    const wartende = offen.get(blobId)
    if (wartende) { wartende.push(aufloesen); return }
    offen.set(blobId, [aufloesen])
    if (geplant === null) geplant = setTimeout(() => void abarbeiten(), FENSTER_MS)
  })
}

/** Nur für Tests: Zwischenspeicher und Warteschlange zurücksetzen. */
export function quellenZuruecksetzen(): void {
  speicher.clear()
  offen.clear()
  if (geplant !== null) { clearTimeout(geplant); geplant = null }
}

async function abarbeiten(): Promise<void> {
  geplant = null
  const stapel = [...offen.keys()].slice(0, MAX_JE_AUFRUF)
  if (stapel.length === 0) return

  const rueckrufe = stapel.map((id) => {
    const r = offen.get(id) ?? []
    offen.delete(id)
    return [id, r] as const
  })

  // Ist mehr angefallen, als in einen Aufruf passt, folgt sofort der nächste.
  // Zwei Aufrufe für hundert Bilder sind immer noch besser als hundert.
  if (offen.size > 0 && geplant === null) {
    geplant = setTimeout(() => void abarbeiten(), 0)
  }

  let urls: Record<string, string> = {}
  try {
    const res = await api.post<{ urls: Record<string, string>; expiresAt: string }>(
      '/blobs/urls', { blobIds: stapel })
    urls = res.urls
    const gueltigBis = Date.parse(res.expiresAt) - PUFFER_MS
    for (const [id, url] of Object.entries(urls)) speicher.set(id, { url, gueltigBis })
  } catch {
    // Offline oder Serverfehler: alle Wartenden bekommen `null` und damit den
    // Platzhalter. Beim nächsten Sichtbarwerden wird es erneut versucht.
  }

  for (const [id, warten] of rueckrufe) {
    for (const r of warten) r(urls[id] ?? null)
  }
}

/**
 * Fällt der Zwischenspeicher weg, ohne dass eine URL abläuft: beim Abmelden.
 *
 * Die Signatur ist an die Sitzung gebunden — nach einem Kontowechsel auf
 * demselben Gerät wären die alten URLs wertlos, aber der Zwischenspeicher
 * wüsste es nicht.
 */
export function quellenVergessen(): void {
  speicher.clear()
}
